/** Admin → Inspection schedules: recurring inspections (daily opening checks, weekly walk-rounds, monthly fire checks). */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate } from "@tanstack/react-router";
import { CalendarClock, Pause, Play, Plus, Zap } from "lucide-react";
import { useState } from "react";
import { api, post } from "../../lib/api";
import { useCan } from "../../lib/session";
import { useSpaces } from "../../lib/work";
import { Card, Empty, ErrorNote, Field, fmtDate, Modal, PageHeader } from "../../components/ui";

interface Schedule {
  id: string;
  name: string | null;
  templateKey: string;
  templateName: string | null;
  siteId: string | null;
  siteName: string | null;
  teamKey: string | null;
  teamName: string | null;
  spaceKey: string;
  frequency: "weekly" | "monthly";
  days: number[];
  monthDay: number | null;
  atTime: string;
  dueMinutes: number;
  active: boolean;
  lastRunFor: string | null;
  lastRunId: string | null;
  lastRunStatus: string | null;
}
type Draft = Omit<Schedule, "id" | "templateName" | "siteName" | "teamName" | "lastRunFor" | "lastRunId" | "lastRunStatus">;
interface Tree { sites: Array<{ id: string; name: string; active: boolean }>; teams: Array<{ id: string; key: string; name: string; active: boolean }> }

const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const ordinal = (n: number) => `${n}${n % 10 === 1 && n !== 11 ? "st" : n % 10 === 2 && n !== 12 ? "nd" : n % 10 === 3 && n !== 13 ? "rd" : "th"}`;

export function describeSchedule(s: Pick<Schedule, "frequency" | "days" | "monthDay" | "atTime">) {
  if (s.frequency === "monthly") return `Monthly on the ${s.monthDay === -1 ? "last day" : ordinal(s.monthDay ?? 1)} at ${s.atTime}`;
  const d = [...s.days].sort();
  if (d.length === 7) return `Every day at ${s.atTime}`;
  if (d.join() === "1,2,3,4,5") return `Weekdays at ${s.atTime}`;
  if (d.join() === "6,7") return `Weekends at ${s.atTime}`;
  return `${d.map((x) => DAYS[x - 1]).join(", ")} at ${s.atTime}`;
}

const dueLabel = (m: number) => (m % 1440 === 0 ? `${m / 1440} day${m === 1440 ? "" : "s"}` : m % 60 === 0 ? `${m / 60} hour${m === 60 ? "" : "s"}` : `${m} min`);

function ScheduleForm({ initial, id, onClose }: { initial: Draft; id?: string; onClose: () => void }) {
  const qc = useQueryClient();
  const [f, setF] = useState<Draft>(initial);
  const set = (p: Partial<Draft>) => setF((x) => ({ ...x, ...p }));
  const templates = useQuery({ queryKey: ["insp-templates"], queryFn: () => api<{ items: Array<{ key: string; name: string; description: string }> }>("/api/inspections/templates") });
  const tree = useQuery({ queryKey: ["dir-tree"], queryFn: () => api<Tree>("/api/directory/tree") });
  const spaces = useSpaces();
  const save = useMutation({
    mutationFn: () => api(id ? `/api/inspections/schedules/${id}` : "/api/inspections/schedules", { method: id ? "PUT" : "POST", json: { ...f, name: f.name || undefined } }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["insp-schedules"] });
      onClose();
    },
  });
  const toggleDay = (d: number) => set({ days: f.days.includes(d) ? f.days.filter((x) => x !== d) : [...f.days, d].sort() });
  return (
    <Modal
      title={id ? "Edit schedule" : "New inspection schedule"}
      onClose={onClose}
      footer={
        <div className="flex justify-end gap-2">
          <button className="btn" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" disabled={save.isPending || !f.templateKey || !f.teamKey || !f.spaceKey} onClick={() => save.mutate()}>{save.isPending ? "Saving…" : "Save"}</button>
        </div>
      }
    >
      <div className="space-y-3">
        <Field label="Inspection">
          <select className="input" value={f.templateKey} onChange={(e) => set({ templateKey: e.target.value })}>
            <option value="">Choose a checklist…</option>
            {templates.data?.items.map((t) => <option key={t.key} value={t.key}>{t.name}</option>)}
          </select>
        </Field>
        <Field label="Name (optional)" hint="Shown on the list, e.g. “Mythos opening check”.">
          <input className="input" value={f.name ?? ""} onChange={(e) => set({ name: e.target.value })} maxLength={120} />
        </Field>
        <Field label="How often">
          <div className="flex gap-2">
            {(["weekly", "monthly"] as const).map((fr) => (
              <button key={fr} type="button" className={`btn ${f.frequency === fr ? "btn-primary" : ""}`} onClick={() => set({ frequency: fr, monthDay: fr === "monthly" ? (f.monthDay ?? 1) : null })}>
                {fr === "weekly" ? "On weekdays I pick" : "Once a month"}
              </button>
            ))}
          </div>
        </Field>
        {f.frequency === "weekly" ? (
          <div className="flex flex-wrap gap-1.5">
            {DAYS.map((d, i) => (
              <button key={d} type="button" className={`btn !min-h-0 !px-2.5 !py-1 text-xs ${f.days.includes(i + 1) ? "btn-primary" : ""}`} onClick={() => toggleDay(i + 1)}>{d}</button>
            ))}
            <button type="button" className="btn !min-h-0 !px-2.5 !py-1 text-xs" onClick={() => set({ days: [1, 2, 3, 4, 5, 6, 7] })}>Every day</button>
          </div>
        ) : (
          <Field label="Day of the month">
            <select className="input" value={f.monthDay ?? 1} onChange={(e) => set({ monthDay: Number(e.target.value) })}>
              {Array.from({ length: 28 }, (_, i) => i + 1).map((d) => <option key={d} value={d}>{ordinal(d)}</option>)}
              <option value={-1}>Last day of the month</option>
            </select>
          </Field>
        )}
        <div className="grid grid-cols-2 gap-3">
          <Field label="Time (Malta)">
            <input type="time" className="input" value={f.atTime} onChange={(e) => set({ atTime: e.target.value })} />
          </Field>
          <Field label="Must be done within">
            <select className="input" value={f.dueMinutes} onChange={(e) => set({ dueMinutes: Number(e.target.value) })}>
              {[30, 60, 120, 240, 480, 1440, 2880, 10080].map((m) => <option key={m} value={m}>{dueLabel(m)}</option>)}
            </select>
          </Field>
        </div>
        <Field label="Who does it" hint="The inspection lands in this team's pool for anyone in the team to take.">
          <select className="input" value={f.teamKey ?? ""} onChange={(e) => set({ teamKey: e.target.value })}>
            <option value="">Choose a team…</option>
            {tree.data?.teams.filter((t) => t.active).map((t) => <option key={t.key} value={t.key}>{t.name}</option>)}
          </select>
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Site">
            <select className="input" value={f.siteId ?? ""} onChange={(e) => set({ siteId: e.target.value || null })}>
              <option value="">No specific site</option>
              {tree.data?.sites.filter((s) => s.active).map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          </Field>
          <Field label="Workspace" hint="Where the task and any fixes are tracked.">
            <select className="input" value={f.spaceKey} onChange={(e) => set({ spaceKey: e.target.value })}>
              <option value="">Choose…</option>
              {spaces.data?.items.map((s) => <option key={s.key} value={s.key}>{s.name}</option>)}
            </select>
          </Field>
        </div>
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={f.active} onChange={(e) => set({ active: e.target.checked })} /> Active
        </label>
        <p className="muted text-xs">{describeSchedule(f)}. Any failed check automatically creates a fix-it task.</p>
        <ErrorNote error={save.error} />
      </div>
    </Modal>
  );
}

const EMPTY: Draft = { name: "", templateKey: "", siteId: null, teamKey: "", spaceKey: "", frequency: "weekly", days: [1, 2, 3, 4, 5, 6, 7], monthDay: null, atTime: "08:00", dueMinutes: 60, active: true };

export function InspectionSchedulesPage() {
  const can = useCan();
  const qc = useQueryClient();
  const nav = useNavigate();
  const admin = can("inspections.template.admin");
  const list = useQuery({ queryKey: ["insp-schedules"], queryFn: () => api<{ items: Schedule[] }>("/api/inspections/schedules") });
  const [editing, setEditing] = useState<{ id?: string; draft: Draft } | null>(null);
  const toggle = useMutation({
    mutationFn: (s: Schedule) => api(`/api/inspections/schedules/${s.id}`, { method: "PUT", json: { ...s, name: s.name ?? undefined, teamKey: s.teamKey ?? "", active: !s.active } }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["insp-schedules"] }),
  });
  const runNow = useMutation({
    mutationFn: (id: string) => post<{ id: string }>(`/api/inspections/schedules/${id}/run-now`, {}),
    onSuccess: (r) => void nav({ to: "/inspections/$id", params: { id: r.id } }),
  });
  return (
    <div className="space-y-4">
      <PageHeader
        title="Inspection schedules"
        subtitle="Recurring checks that ONE starts automatically and puts in the right team's pool."
        actions={admin && <button className="btn btn-primary" onClick={() => setEditing({ draft: EMPTY })}><Plus size={16} /> New schedule</button>}
      />
      <ErrorNote error={toggle.error ?? runNow.error} />
      {list.isLoading ? <Empty>Loading…</Empty> : !list.data?.items.length ? (
        <Card><Empty>No schedules yet. Create one for a daily opening check or a monthly safety check.</Empty></Card>
      ) : (
        <Card>
          <div className="overflow-x-auto">
            <table className="table">
              <thead>
                <tr><th>Inspection</th><th>When</th><th>Team</th><th>Site</th><th>Last run</th><th /></tr>
              </thead>
              <tbody>
                {list.data.items.map((s) => (
                  <tr key={s.id} className={s.active ? "" : "opacity-55"}>
                    <td>
                      <div className="font-medium">{s.name || s.templateName || s.templateKey}</div>
                      {s.name && <div className="muted text-xs">{s.templateName}</div>}
                      {!s.active && <span className="chip mt-1 text-xs">Paused</span>}
                    </td>
                    <td className="whitespace-nowrap"><CalendarClock size={14} className="muted mr-1 inline" />{describeSchedule(s)}<div className="muted text-xs">due within {dueLabel(s.dueMinutes)}</div></td>
                    <td>{s.teamName ?? "—"}</td>
                    <td>{s.siteName ?? "—"}</td>
                    <td className="whitespace-nowrap">
                      {s.lastRunId ? <Link to="/inspections/$id" params={{ id: s.lastRunId }} className="hover:underline">{fmtDate(s.lastRunFor ?? "")}</Link> : <span className="muted">Not yet</span>}
                      {s.lastRunStatus && <div className="muted text-xs">{s.lastRunStatus}</div>}
                    </td>
                    <td className="whitespace-nowrap text-right">
                      {admin && (
                        <div className="flex justify-end gap-1.5">
                          <button className="btn !min-h-0 !px-2 !py-1 text-xs" title="Start one now" disabled={runNow.isPending} onClick={() => runNow.mutate(s.id)}><Zap size={13} /> Run now</button>
                          <button className="btn !min-h-0 !px-2 !py-1 text-xs" title={s.active ? "Pause" : "Resume"} onClick={() => toggle.mutate(s)}>{s.active ? <Pause size={13} /> : <Play size={13} />}</button>
                          <button className="btn !min-h-0 !px-2 !py-1 text-xs" onClick={() => setEditing({ id: s.id, draft: { name: s.name ?? "", templateKey: s.templateKey, siteId: s.siteId, teamKey: s.teamKey ?? "", spaceKey: s.spaceKey, frequency: s.frequency, days: s.days, monthDay: s.monthDay, atTime: s.atTime, dueMinutes: s.dueMinutes, active: s.active } })}>Edit</button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}
      {editing && <ScheduleForm id={editing.id} initial={editing.draft} onClose={() => setEditing(null)} />}
    </div>
  );
}
